import React, { createContext, useContext, useReducer, useState, ReactNode } from "react";
import { 
  Exercise as UIExercise, 
  ExerciseType,
  MultipleChoiceData
} from "@/components/exercises/exercise.interfaces";
import {
  exerciseService,
  updateExercisesOrder,
} from "@/services/admin/exercises.service";
import { useAppToast } from "@/hooks/useAppToast";

// =============== Estado do Contexto ===============

// Estado inicial e tipo de estado para o gerenciamento de exercícios
interface ExerciseState {
  exercises: UIExercise[];
  isRefreshing: boolean;
  error: string | null;
}

// =============== Actions para o Reducer ===============

// Enum para tipos de ação
enum ActionType {
  FETCH_EXERCISES_START = 'FETCH_EXERCISES_START',
  FETCH_EXERCISES_SUCCESS = 'FETCH_EXERCISES_SUCCESS',
  FETCH_EXERCISES_ERROR = 'FETCH_EXERCISES_ERROR',
  ADD_EXERCISE = 'ADD_EXERCISE',
  UPDATE_EXERCISE = 'UPDATE_EXERCISE',
  DELETE_EXERCISE = 'DELETE_EXERCISE',
  SET_EXERCISES = 'SET_EXERCISES',
  BATCH_UPDATE_START = 'BATCH_UPDATE_START',
  BATCH_UPDATE_SUCCESS = 'BATCH_UPDATE_SUCCESS',
  BATCH_UPDATE_ERROR = 'BATCH_UPDATE_ERROR',
  SORT_UPDATE_START = 'SORT_UPDATE_START',
  SORT_UPDATE_SUCCESS = 'SORT_UPDATE_SUCCESS',
  SORT_UPDATE_ERROR = 'SORT_UPDATE_ERROR'
}

// Tipos de ações
type ExerciseAction =
  | { type: ActionType.FETCH_EXERCISES_START }
  | { type: ActionType.FETCH_EXERCISES_SUCCESS; payload: UIExercise[] }
  | { type: ActionType.FETCH_EXERCISES_ERROR; payload: string }
  | { type: ActionType.ADD_EXERCISE; payload: UIExercise }
  | { type: ActionType.UPDATE_EXERCISE; payload: UIExercise }
  | { type: ActionType.DELETE_EXERCISE; payload: string }
  | { type: ActionType.SET_EXERCISES; payload: UIExercise[] }
  | { type: ActionType.BATCH_UPDATE_START }
  | { type: ActionType.BATCH_UPDATE_SUCCESS }
  | { type: ActionType.BATCH_UPDATE_ERROR; payload: string }
  | { type: ActionType.SORT_UPDATE_START }
  | { type: ActionType.SORT_UPDATE_SUCCESS; payload: UIExercise[] }
  | { type: ActionType.SORT_UPDATE_ERROR; payload: string };

// Reducer function
const exerciseReducer = (state: ExerciseState, action: ExerciseAction): ExerciseState => {
  switch (action.type) {
    case ActionType.FETCH_EXERCISES_START:
    case ActionType.BATCH_UPDATE_START:
    case ActionType.SORT_UPDATE_START:
      return {
        ...state,
        isRefreshing: true,
        error: null
      };
    case ActionType.FETCH_EXERCISES_SUCCESS:
    case ActionType.SORT_UPDATE_SUCCESS:
      return {
        ...state,
        exercises: action.payload,
        isRefreshing: false,
        error: null
      };
    case ActionType.BATCH_UPDATE_SUCCESS:
      return {
        ...state,
        isRefreshing: false,
        error: null
      };
    case ActionType.FETCH_EXERCISES_ERROR:
    case ActionType.BATCH_UPDATE_ERROR:
    case ActionType.SORT_UPDATE_ERROR:
      return {
        ...state,
        isRefreshing: false,
        error: action.payload
      };
    case ActionType.ADD_EXERCISE:
      return {
        ...state,
        exercises: [...state.exercises, action.payload]
      };
    case ActionType.UPDATE_EXERCISE:
      return {
        ...state,
        exercises: state.exercises.map(exercise => 
          exercise.id === action.payload.id ? action.payload : exercise
        )
      };
    case ActionType.DELETE_EXERCISE:
      return {
        ...state,
        exercises: state.exercises.filter(exercise => exercise.id !== action.payload)
      };
    case ActionType.SET_EXERCISES:
      return {
        ...state,
        exercises: action.payload
      };
    default:
      return state;
  }
};

// =============== Interface do Contexto ===============

// Context type com os métodos e estado do contexto unificado
interface ExerciseContextType {
  // Propriedades do contexto de módulo
  moduleId: string;
  state: ExerciseState;
  
  // Métodos de gerenciamento de exercícios do módulo
  refreshExercises: () => Promise<boolean>;
  addExercise: (exercise: UIExercise) => Promise<boolean>;
  updateExercise: (exercise: UIExercise) => Promise<boolean>;
  deleteExercise: (id: string) => Promise<boolean>;
  setExercises: (exercises: UIExercise[]) => void;
  batchUpdateExercises: () => Promise<boolean>;
  batchUpdateExercisesSortOrder: (newOrder: (string | number)[]) => Promise<boolean>;
  
  // Propriedades do contexto de diálogo de exercício
  dialogOpen: boolean;
  editingExercise?: UIExercise;
  
  // Métodos de controle do diálogo
  openDialog: (exercise?: UIExercise) => void;
  closeDialog: () => void;
}

// =============== Criação do Contexto ===============

const ExerciseContext = createContext<ExerciseContextType | undefined>(undefined);

export const useExerciseContext = () => {
  const context = useContext(ExerciseContext);
  if (context === undefined) {
    throw new Error("useExerciseContext must be used within an ExerciseProvider");
  }
  return context;
};

// =============== Provider do Contexto ===============

interface ExerciseProviderProps {
  children: ReactNode;
  moduleId: string;
}

export const ExerciseProvider: React.FC<ExerciseProviderProps> = ({
  children,
  moduleId,
}) => {
  const toast = useAppToast();
  
  // Estado inicial para gerenciamento de exercícios
  const initialState: ExerciseState = {
    exercises: [],
    isRefreshing: false,
    error: null
  };

  // Estado para o reducer de exercícios
  const [state, dispatch] = useReducer(exerciseReducer, initialState);
  
  // Estado para controle do diálogo de exercício
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<UIExercise | undefined>();

  // Métodos para controle do diálogo
  const openDialog = (exercise?: UIExercise) => {
    if (exercise) {
      // Com o novo modelo, apenas usa o exercício como está
      // Os dados já estão tipados corretamente no campo 'data'
      setEditingExercise({ ...exercise });
    } else {
      // Cria um exercício novo com dados tipados padrão para múltipla escolha
      const defaultMultipleChoiceData: MultipleChoiceData = {
        question: "",
        options: [{ id: "1", text: "", isCorrect: true }],
        multipleCorrectAnswers: false
      };

      setEditingExercise({
        type: ExerciseType.MultipleChoice,
        data: defaultMultipleChoiceData
      } as UIExercise);
    }
    
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingExercise(undefined);
  };

  // Métodos para interagir com o estado e os serviços
  const refreshExercises = async (): Promise<boolean> => {
    try {
      dispatch({ type: ActionType.FETCH_EXERCISES_START });

    const updatedExercises = await exerciseService.listComponentExercisesByModule(
      moduleId
    );

    // Com a nova guideline, o serviço já retorna os dados no formato correto
      // Como são compatíveis, apenas precisamos fazer cast para o tipo do contexto
      const moduleExercises = updatedExercises as UIExercise[];

      dispatch({ type: ActionType.FETCH_EXERCISES_SUCCESS, payload: moduleExercises });
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      console.error("Error refreshing exercises:", error);
      toast.error("Failed to refresh exercises");
      dispatch({ type: ActionType.FETCH_EXERCISES_ERROR, payload: errorMessage });
      return false;
    }
  };

  const addExercise = async (exercise: UIExercise): Promise<boolean> => {
    try {
      const newExercise = await exerciseService.createComponentExercise(
        moduleId,
        exercise
      );
      if (newExercise) {
        await refreshExercises(); // Recarrega para obter a lista atualizada
        toast.success("Exercício adicionado com sucesso!");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Erro ao adicionar exercício:", error);
      toast.error("Não foi possível adicionar o exercício.");
      return false;
    }
  };

  const updateExercise = async (exercise: UIExercise): Promise<boolean> => {
    if (!exercise.id) return false;

    try {
      // Converte ID para string se necessário
      const exerciseId = typeof exercise.id === 'string' ? exercise.id : String(exercise.id);

      const updatedExercise = await exerciseService.updateComponentExercise(
        exerciseId,
        exercise,
        moduleId
      );
      if (updatedExercise) {
        await refreshExercises(); // Recarrega para obter a lista atualizada
        toast.success("Exercício atualizado com sucesso!");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Erro ao atualizar exercício:", error);
      toast.error("Não foi possível atualizar o exercício.");
      return false;
    }
  };

  const deleteExercise = async (id: string): Promise<boolean> => {
    try {
      await exerciseService.deleteComponentExercise(id, moduleId);
      await refreshExercises(); // Recarrega para obter a lista atualizada
      toast.success("Exercício excluído com sucesso!");
      return true;
    } catch (error) {
      console.error("Erro ao excluir exercício:", error);
      toast.error("Não foi possível excluir o exercício.");
      return false;
    }
  };

  // Método utilitário para atualizar diretamente o estado (usado para sincronização)
  const setExercises = (exercises: UIExercise[]) => {
    dispatch({ type: ActionType.SET_EXERCISES, payload: exercises });
  };

  // Novo método para operações em lote
  const batchUpdateExercises = async (): Promise<boolean> => {
    try {
      dispatch({ type: ActionType.BATCH_UPDATE_START });
      // Lógica para preparar e enviar os dados em lote
      // const updateData = exercises.map(ex => ({ id: ex.id, ... }));
      // await exerciseService.batchUpdate(updateData);
      dispatch({ type: ActionType.BATCH_UPDATE_SUCCESS });
      return true;
    } catch (error) {
      console.error("Erro ao atualizar exercícios em lote:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Ocorreu um erro";
      dispatch({ type: ActionType.BATCH_UPDATE_ERROR, payload: errorMessage });
      return false;
    }
  };

  const batchUpdateExercisesSortOrder = async (
    newOrder: (string | number)[]
  ): Promise<boolean> => {
    try {
      dispatch({ type: ActionType.SORT_UPDATE_START });

      const stringOrder = newOrder.map(String);
      await updateExercisesOrder(moduleId, stringOrder);
      await refreshExercises(); // Recarrega para obter a lista atualizada

      toast.success("Ordem dos exercícios atualizada!");
      return true;
    } catch (error) {
      console.error("Erro ao atualizar a ordem:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Ocorreu um erro";
      dispatch({ type: ActionType.SORT_UPDATE_ERROR, payload: errorMessage });
      return false;
    }
  };

  // Combina todos os valores do contexto em um único objeto
  const contextValue: ExerciseContextType = {
    // Contexto do módulo
    moduleId,
    state,
    refreshExercises,
    addExercise,
    updateExercise,
    deleteExercise,
    setExercises,
    batchUpdateExercises,
    batchUpdateExercisesSortOrder,
    
    // Contexto do diálogo
    dialogOpen,
    editingExercise,
    openDialog,
    closeDialog
  };

  return (
    <ExerciseContext.Provider value={contextValue}>
      {children}
    </ExerciseContext.Provider>
  );
};
